import {
  useConfigurationQuery,
  useActiveRoundingSessionQuery,
  useDepartmentsQuery,
  useDepartmentGroupsQuery,
  useDispositionsQuery,
  useUserPreferencesQuery,
} from "../../api/queries";

import { QueryClient, QueryClientProvider } from "react-query";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import ListAPIContext from ".";

const ListAPIContextProvider: FC = ({ children }) => {
  const [analytics, setAnalytics] = useState<AnalyticsBrowser | null>(null);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);

  const departmentsQuery = useDepartmentsQuery();
  const departmentGroupsQuery = useDepartmentGroupsQuery();

  const configurationQuery = useConfigurationQuery();

  const activeRoundingSessionQuery = useActiveRoundingSessionQuery();

  const dispositionsQuery = useDispositionsQuery();

  const userPreferencesQuery = useUserPreferencesQuery();

  useEffect(() => {
    if (!configurationQuery.isSuccess) return;
    const appSettings = configurationQuery.data?.[0]?.app_settings;
    if (!appSettings?.fe_segment_write_key) return;
    const analyticsInit = AnalyticsBrowser.load({
      writeKey: appSettings.fe_segment_write_key,
    });
    const urlParams = new URLSearchParams(window.location.search);
    analyticsInit.identify(urlParams.get("emr_user_id"), {
      username: urlParams.get("emr_user_full_name"),
      email: urlParams.get("emr_user_email"),
      role: urlParams.get("emr_user_role"),
      service_area: urlParams.get("emr_user_service_area"),
      dept_speciality: urlParams.get("emr_user_dept_speciality"),
      stack: window.location.host.split(".")[0],
      view: "patient_list",
    });
    setAnalytics(analyticsInit);
  }, [configurationQuery.isSuccess, configurationQuery.data]);

  return (
    <ListAPIContext.Provider
      value={{
        departmentsQuery,
        departmentGroupsQuery,
        configurationQuery,
        activeRoundingSessionQuery,
        dispositionsQuery,
        userPreferencesQuery,
        analytics,
        showErrorToast,
        setShowErrorToast,
      }}
    >
      {children}
    </ListAPIContext.Provider>
  );
};

const WrappedListAPIContextProvider: FC = ({ children }) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnmount: false,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        staleTime: Infinity,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ListAPIContextProvider>{children}</ListAPIContextProvider>
    </QueryClientProvider>
  );
};

export default WrappedListAPIContextProvider;
